import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { CalendarCheck, Money, Users, Clock, CheckCircle, ArrowUpRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

function Kpi({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = { primary: "text-primary bg-primary/10", success: "text-success bg-success/10", warn: "text-warning bg-warning/10", danger: "text-destructive bg-destructive/10" };
  return (
    <div data-testid={testid} className="bg-card border border-border rounded-md p-5 flex flex-col justify-between">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">{label}</span>
        <span className={`h-8 w-8 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon size={17} weight="bold" /></span>
      </div>
      <p className="text-3xl font-heading font-semibold tracking-tight mt-4 tnums">{value}</p>
    </div>
  );
}
const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" });

export default function ClinicReports() {
  const { money } = useAuth();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);

  useEffect(() => { api.get("/clinic/reports").then(({ data }) => setRep(data)); }, []);
  if (!rep) return <div className="p-8"><div className="h-6 w-40 bg-muted rounded animate-pulse" /></div>;

  const series = rep.series.map((s) => ({ ...s, label: fmtDate(s.date) }));

  return (
    <div data-testid="clinic-reports-page">
      <PageHeader title="Clinic reports" subtitle="Appointments, revenue and patient activity" />
      <div className="p-6 md:p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Kpi testid="kpi-today-appts" icon={CalendarCheck} label="Today's appointments" value={rep.today.count} />
          <Kpi testid="kpi-revenue-today" icon={Money} label="Revenue today" value={money(rep.revenue.today)} tone="success" />
          <Kpi testid="kpi-outstanding" icon={Clock} label="Outstanding" value={money(rep.revenue.outstanding)} tone="warn" />
          <Kpi testid="kpi-patients" icon={Users} label="Total patients" value={rep.patients.total} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-card border border-border rounded-md p-6">
            <h3 className="text-lg font-heading font-medium mb-5">Revenue — last 14 days</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={series} margin={{ left: 0, right: 8 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(150 6% 42%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(150 6% 42%)" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip cursor={{ fill: "hsl(150 8% 95%)" }} formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, border: "1px solid hsl(120 8% 89%)", fontSize: 12 }} />
                <Bar dataKey="revenue" radius={[3, 3, 0, 0]} barSize={16}>{series.map((_, i) => <Cell key={i} fill="hsl(155 24% 33%)" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-card border border-border rounded-md p-6">
            <h3 className="text-lg font-heading font-medium mb-4">Revenue this month</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">This week</span><span className="tnums font-medium">{money(rep.revenue.week)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">This month</span><span className="tnums font-medium">{money(rep.revenue.month)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Invoiced (month)</span><span className="tnums font-medium">{money(rep.revenue.invoiced_month)}</span></div>
            </div>
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mt-5 mb-2">By method (month)</h4>
            {Object.keys(rep.revenue_by_method).length === 0 ? <p className="text-sm text-muted-foreground">No payments yet.</p> : (
              <div className="space-y-1.5 text-sm">
                {Object.entries(rep.revenue_by_method).map(([m, v]) => (
                  <div key={m} className="flex justify-between capitalize"><span className="text-muted-foreground">{m.replace("_", " ")}</span><span className="tnums">{money(v)}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-md p-6">
            <h3 className="text-lg font-heading font-medium mb-4">Today by doctor</h3>
            {rep.today.by_doctor.length === 0 ? <p className="text-sm text-muted-foreground py-4">No doctors / appointments today.</p> : (
              <div className="divide-y divide-border">
                {rep.today.by_doctor.map((d) => (
                  <div key={d.doctor_id} className="flex items-center justify-between py-2.5 text-sm">
                    <span className="font-medium">{d.doctor_name}</span>
                    <span className="text-muted-foreground flex items-center gap-3">
                      <span className="flex items-center gap-1"><CheckCircle size={14} className="text-success" /> {d.completed}</span>
                      <span className="flex items-center gap-1"><Clock size={14} className="text-primary" /> {d.scheduled}</span>
                      <span>/ {d.total}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-medium">Upcoming (7 days)</h3>
              <button onClick={() => navigate("/clinic/appointments")} className="text-sm text-primary hover:underline flex items-center gap-1">Schedule <ArrowUpRight size={14} /></button>
            </div>
            {rep.upcoming.length === 0 ? <p className="text-sm text-muted-foreground py-4">Nothing scheduled.</p> : (
              <div className="divide-y divide-border">
                {rep.upcoming.slice(0, 8).map((a) => (
                  <button key={a.id} onClick={() => navigate(`/clinic/patients/${a.patient_id}`)} className="w-full text-left flex items-center justify-between py-2.5 text-sm hover:text-primary">
                    <span>{fmtDate(a.date)} · {a.time} — {a.patient_name}</span>
                    <span className="text-xs text-muted-foreground">{a.doctor_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
